/** @prettier */
import React, { useState, useEffect } from "react";
import { QuestionTypeCounts, getQuestionTypeCounts } from "./createTestApi";
import { listGroupSelection } from "./util/listGroupSelection";
import { FormGroup, ControlLabel, ListGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { QuestionType } from "../models";

type State<T> = [T, (t: T) => void];

export type QuestionTypeCountsState =
    | { state: "loading" }
    | { state: "nothingSelected" }
    | { state: "loaded"; data: QuestionTypeCounts }
    | { state: "error"; error: string };

export function useQuestionTypeCounts(
    userUuid: string,
    selectedCategories: Set<number>,
): QuestionTypeCountsState {
    const [questionTypes, setState] = useState<QuestionTypeCountsState>({
        state: "nothingSelected",
    });
    useEffect(() => {
        if (selectedCategories.size) {
            getQuestionTypeCounts(userUuid, Array.from(selectedCategories))
                .then(counts => setState({ state: "loaded", data: counts }))
                .catch(e => {
                    console.error("Error getting question types", e);
                    setState({ state: "error", error: e.message });
                });
        } else {
            setState({ state: "nothingSelected" });
        }
    }, [selectedCategories, userUuid]);

    return questionTypes;
}

type QuestionTypeFormGroupProps = {
    counts: QuestionTypeCountsState;
    state: State<QuestionType>;
};
const QuestionTypeFormGroup = ({
    counts,
    state,
}: QuestionTypeFormGroupProps) => (
    <FormGroup>
        <ControlLabel>Question Type</ControlLabel>
        {counts.state === "nothingSelected" ? (
            <div>Select categories above.</div>
        ) : counts.state === "loading" ? (
            <div>
                <FontAwesomeIcon icon="spinner" size="2x" spin />
            </div>
        ) : counts.state === "error" ? (
            <div>An error has occurred loading the question counts.</div>
        ) : (
            <ListGroup>
                {listGroupSelection(`All (${counts.data.all})`, "all", state)}
                {listGroupSelection(
                    `Unused (${counts.data.unused})`,
                    "unused",
                    state,
                )}
                {listGroupSelection(
                    `Incorrect (${counts.data.incorrect})`,
                    "incorrect",
                    state,
                )}
                {listGroupSelection(
                    `Correct (${counts.data.correct})`,
                    "correct",
                    state,
                )}
                {listGroupSelection(
                    `Unanswered (${counts.data.unanswered})`,
                    "unanswered",
                    state,
                )}
                {listGroupSelection(
                    `Flagged (${counts.data.flagged})`,
                    "flagged",
                    state,
                )}
            </ListGroup>
        )}
    </FormGroup>
);
export default QuestionTypeFormGroup;
